import React from "react";
import { Field, TYPE_META, qtyError } from "./editorShared";
import { BookMarked, Bookmark, GripVertical, Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import UnitSelect from "../UnitSelect";

export default function LineItemsSection({ addItem, openSaveItem, taxSettings, q, removeItem, setImportOpen, updateItem }) {
  return (
    <section className="bg-card border border-slate-200/80 rounded-xl p-4 sm:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3">
        <h2 className="text-base font-semibold text-slate-900">Voci del Preventivo</h2>
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
          <Button size="sm" data-testid="quote-editor-import-catalog-button" onClick={() => setImportOpen(true)}>
            <BookMarked style={{ width: 15, height: 15 }} className="mr-1.5" /><span className="sm:hidden">Dal Listino</span><span className="hidden sm:inline">Importa dal Listino</span>
          </Button>
          <Button variant="outline" size="sm" data-testid="quote-editor-add-item-button" onClick={addItem}>
            <Plus style={{ width: 15, height: 15 }} className="mr-1.5" /> Aggiungi voce
          </Button>
        </div>
      </div>
      <div className="space-y-4">
        {q.items.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-8">Nessuna voce. Aggiungi la prima riga del preventivo.</p>
        )}
        {q.items.map((it, idx) => {
          const Meta = TYPE_META[it.item_type] || TYPE_META.base;
          return (
            <div key={it.id} data-testid={`editor-item-${idx}`} className="rounded-xl border border-border p-4 bg-slate-50/40">
              <div className="flex flex-col-reverse sm:flex-row sm:items-start gap-2 sm:gap-3">
                <GripVertical className="hidden sm:block w-4 h-4 text-slate-300 mt-2.5 shrink-0" />
                <div className="flex-1 min-w-0 space-y-4">
                  <div className="grid sm:grid-cols-[1fr_240px] gap-4">
                    <Field label="Nome del Servizio / Prestazione">
                      <Input value={it.title} onChange={(e) => updateItem(idx, { title: e.target.value })} placeholder="Es. Realizzazione Sito Web, Servizio Fotografico, Consulenza SEO..." />
                    </Field>
                    <Field label="Tipo di voce">
                      <Select value={it.item_type} onValueChange={(v) => updateItem(idx, { item_type: v })}>
                        <SelectTrigger data-testid={`quote-editor-item-type-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="base">Fissa / Obbligatoria (Inclusa)</SelectItem>
                          <SelectItem value="optional" data-testid="quote-editor-item-toggle-optional">Opzionale (Add-on con switch Sì/No)</SelectItem>
                          <SelectItem value="variable">Quantità Modificabile dal Cliente (+/-)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p data-testid={`item-type-help-${idx}`} className="text-xs text-slate-500 mt-1.5 leading-snug">{Meta.help}</p>
                    </Field>
                  </div>
                  <Field label="Descrizione">
                    <Textarea value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} rows={2} />
                  </Field>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <Field label="Prezzo unit. (€)">
                      <Input type="number" min="0" step="0.01" value={it.unit_price} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="font-mono" />
                    </Field>
                    <Field label="Quantità">
                      <Input type="number" min="0" step="any" data-testid={`quote-editor-item-qty-${idx}`} value={it.quantity} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className={`font-mono ${qtyError(it) ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                    </Field>
                    <Field label="Unità di misura">
                      <UnitSelect value={it.unit ?? "corpo"} onChange={(v) => updateItem(idx, { unit: v })} testid={`quote-editor-item-unit-${idx}`} />
                    </Field>
                    <Field label="Tempo di consegna" className="col-span-2 sm:col-span-3">
                      <div className="flex gap-2 min-w-0 max-w-sm">
                        <Input type="number" min="0" data-testid={`quote-editor-item-work-days-${idx}`} value={it.work_days} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(idx, { work_days: e.target.value })} className="font-mono w-20 shrink-0" />
                        <Select value={it.work_unit || "giorni"} onValueChange={(v) => updateItem(idx, { work_unit: v })}>
                          <SelectTrigger data-testid={`quote-editor-item-work-unit-${idx}`} className="flex-1 min-w-0 [&>span]:truncate"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="giorni">Giorni lavorativi</SelectItem>
                            <SelectItem value="ore">Ore</SelectItem>
                            <SelectItem value="settimane">Settimane</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <Field label="Sconto">
                      <Select value={it.discount_type || "none"} onValueChange={(v) => updateItem(idx, { discount_type: v })}>
                        <SelectTrigger data-testid={`quote-editor-item-discount-type-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuno</SelectItem>
                          <SelectItem value="percent">Percentuale %</SelectItem>
                          <SelectItem value="amount">Importo €</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label={it.discount_type === "percent" ? "Sconto %" : "Sconto €"}>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        data-testid={`quote-editor-item-discount-value-${idx}`}
                        value={it.discount_value}
                        disabled={!it.discount_type || it.discount_type === "none"}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => updateItem(idx, { discount_value: e.target.value })}
                        className="font-mono disabled:opacity-50"
                      />
                    </Field>
                    {taxSettings.tax_regime === "ordinario" && (
                      <Field label="Aliquota IVA">
                        <Select value={String(it.iva_rate ?? 22)} onValueChange={(v) => updateItem(idx, { iva_rate: Number(v) })}>
                          <SelectTrigger data-testid={`quote-editor-item-iva-rate-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="22">22%</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="4">4%</SelectItem>
                            <SelectItem value="0">0% (esente)</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    )}
                  </div>
                  {it.item_type === "variable" && (
                    <p className="text-xs text-slate-500 -mb-2">Il cliente potrà scegliere la quantità tra min e max e firmare subito, oppure inviarti una richiesta di aggiornamento (riceverai notifica ed email).</p>
                  )}
                  {it.item_type === "variable" && (
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Q.tà minima">
                        <Input type="number" min="1" value={it.min_qty} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(idx, { min_qty: e.target.value })} className="font-mono" />
                      </Field>
                      <Field label="Q.tà massima">
                        <Input type="number" min="1" value={it.max_qty} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(idx, { max_qty: e.target.value })} className="font-mono" />
                      </Field>
                    </div>
                  )}
                  {qtyError(it) && (
                    <p data-testid={`quote-editor-item-qty-error-${idx}`} className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      {qtyError(it)}
                    </p>
                  )}
                  {it.item_type === "optional" && (
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input type="checkbox" checked={it.enabled_default !== false} onChange={(e) => updateItem(idx, { enabled_default: e.target.checked })} className="rounded border-slate-300" />
                      Attiva di default nella vista cliente
                    </label>
                  )}
                </div>
                <div className="flex sm:flex-col gap-1 shrink-0 self-end sm:self-auto -mt-1 sm:mt-0">
                  <Button variant="ghost" size="icon" onClick={() => openSaveItem(it)} data-testid={`editor-save-item-catalog-${idx}`} className="text-slate-400 hover:text-primary" title="Salva nel Listino">
                    <Bookmark style={{ width: 16, height: 16 }} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => removeItem(idx)} data-testid={`editor-remove-item-${idx}`} className="text-slate-400 hover:text-red-600">
                    <Trash2 style={{ width: 16, height: 16 }} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
